/**
 * Stable per-tab session ID for retail funnel telemetry.
 *
 * Used by `/api/retail-events` so a single browsing session that fires
 * (ViewContent × 5) + (AddToCart × 2) + (InitiateCheckout × 1) +
 * (Purchase × 1) all share the same `sessionId`. Admin analytics can
 * then dedupe by session for "unique users in funnel stage X" or
 * count events for "raw event volume in stage X".
 *
 * Persistence: `sessionStorage` (cleared when the tab closes), so a
 * customer who returns tomorrow gets a fresh session. This is exactly
 * the granularity we want for funnel analysis — same as how Clarity
 * defines a session.
 *
 * Safe to call from anywhere in the browser; SSR-safe (returns empty
 * string during server render).
 */
const STORAGE_KEY = "eql_retail_session";

export function getOrCreateRetailSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    // `crypto.randomUUID` is available in every browser we support
    // (Safari 15.4+, Chrome 92+, Firefox 95+). Fallback to a
    // timestamp + Math.random combo for the ancient browsers that
    // somehow bypass our compatibility targets.
    const id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage can throw in private-browsing modes on some
    // engines. Telemetry is non-critical — return a one-shot ID so the
    // event still has a unique sessionId field (just won't deduplicate
    // across calls in the same tab).
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
